import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type Props = {
  open: boolean;
  onClose: () => void;
};
export function DatenschutzModal({
  open,
  onClose
}: Props) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-14">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-15">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-petrol-dark/80 backdrop-blur-sm" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-16" />

          {/* Modal */}
          <motion.div role="dialog" aria-modal="true" aria-labelledby="datenschutz-title" className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-white rounded-lg shadow-2xl" initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-17">
            {/* Header */}
            <div className="sticky top-0 flex items-center justify-between px-8 py-6 bg-white border-b border-slate-100" data-id="element-18">
              <div className="flex items-center gap-4" data-id="element-19">
                <div className="h-px w-8 bg-hoefelmeyer-green" data-id="element-20"></div>
                <h2 id="datenschutz-title" className="text-2xl font-extrabold text-petrol tracking-tight" data-id="element-21">
                  Datenschutzerklärung
                </h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Schließen" className="w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:text-petrol hover:bg-slate-100 transition-colors" data-id="element-22">
                <XIcon size={20} strokeWidth={1.5} data-id="element-23" />
              </button>
            </div>

            {/* Content */}
            <div className="px-8 py-8 space-y-8 text-slate-700 text-sm leading-relaxed font-light" data-id="element-24">
              <section data-id="element-25">
                <h3 className="text-base font-bold text-petrol mb-3" data-id="element-26">
                  1. Verantwortlicher
                </h3>
                <p data-id="element-27">
                  Die verantwortliche Stelle für die Datenverarbeitung auf
                  dieser Website ist:
                </p>
                <p className="mt-3" data-id="element-28">
                  Höfelmeyer Waagen GmbH
                  <br data-id="element-29" />
                  Werner-von-Siemens-Straße 33
                  <br data-id="element-30" />
                  49124 Georgsmarienhütte
                </p>
                <p className="mt-3" data-id="element-31">
                  <span className="text-slate-500 w-20 inline-block" data-id="element-32">Tel:</span>
                  +49 5401 4977 0
                </p>
                <p data-id="element-33">
                  <span className="text-slate-500 w-20 inline-block" data-id="element-34">
                    E-Mail:
                  </span>
                  waagen(at)hoefelmeyer.de
                </p>
                <p className="mt-3" data-id="element-35">
                  Verantwortliche Stelle ist die natürliche oder juristische
                  Person, die allein oder gemeinsam mit anderen über die Zwecke
                  und Mittel der Verarbeitung von personenbezogenen Daten (z. B.
                  Namen, E-Mail-Adressen o. Ä.) entscheidet.
                </p>
              </section>

              <section data-id="element-36">
                <h3 className="text-base font-bold text-petrol mb-3" data-id="element-37">
                  2. Hosting
                </h3>
                <p data-id="element-38">
                  Diese Website wird bei Amazon Web Services (AWS) gehostet.
                </p>
                <p className="mt-3" data-id="element-39">Anbieter in Europa ist:</p>
                <p className="mt-2" data-id="element-40">
                  Amazon Web Services EMEA SARL
                  <br data-id="element-41" />
                  38 Avenue John F. Kennedy
                  <br data-id="element-42" />
                  L-1855 Luxemburg
                </p>
                <p className="mt-3" data-id="element-43">
                  Beim Aufruf dieser Website werden durch den Hosting-Anbieter
                  automatisch folgende Daten verarbeitet:
                </p>
                <ul className="mt-2 list-disc list-inside space-y-1" data-id="element-44">
                  <li data-id="element-45">IP-Adresse</li>
                  <li data-id="element-46">Datum und Uhrzeit des Zugriffs</li>
                  <li data-id="element-47">Browsertyp und Browserversion</li>
                  <li data-id="element-48">verwendetes Betriebssystem</li>
                  <li data-id="element-49">Referrer-URL</li>
                  <li data-id="element-50">Hostname des zugreifenden Rechners</li>
                </ul>
                <p className="mt-3" data-id="element-51">
                  Diese Daten werden in sogenannten Server-Logfiles gespeichert.
                </p>
                <p className="mt-3" data-id="element-52">
                  Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit.
                  f DSGVO (berechtigtes Interesse an der sicheren und stabilen
                  Bereitstellung der Website).
                </p>
                <p className="mt-3" data-id="element-53">
                  Mit AWS besteht ein Vertrag zur Auftragsverarbeitung gemäß
                  Art. 28 DSGVO.
                </p>
                <p className="mt-3" data-id="element-54">
                  Sofern eine Verarbeitung außerhalb der Europäischen Union
                  erfolgt, geschieht dies auf Grundlage geeigneter Garantien
                  gemäß Art. 44 ff. DSGVO (insbesondere
                  EU-Standardvertragsklauseln).
                </p>
              </section>

              <section data-id="element-55">
                <h3 className="text-base font-bold text-petrol mb-3" data-id="element-56">
                  3. Cookies
                </h3>
                <p data-id="element-57">
                  Diese Website verwendet ausschließlich technisch notwendige
                  Cookies, soweit dies für den Betrieb erforderlich ist.
                </p>
                <p className="mt-3" data-id="element-58">
                  Es werden keine Analyse-, Tracking- oder Marketing-Cookies
                  eingesetzt.
                </p>
                <p className="mt-3" data-id="element-59">
                  Eine Einwilligung über ein Cookie-Banner ist daher nicht
                  erforderlich.
                </p>
              </section>

              <section data-id="element-60">
                <h3 className="text-base font-bold text-petrol mb-3" data-id="element-61">
                  4. Speicherdauer
                </h3>
                <p data-id="element-62">
                  Server-Logfiles werden vom Hosting-Anbieter automatisch nach
                  einem angemessenen Zeitraum gelöscht.
                </p>
              </section>

              <section data-id="element-63">
                <h3 className="text-base font-bold text-petrol mb-3" data-id="element-64">
                  5. Ihre Rechte
                </h3>
                <p data-id="element-65">Sie haben das Recht auf:</p>
                <ul className="mt-2 list-disc list-inside space-y-1" data-id="element-66">
                  <li data-id="element-67">Auskunft (Art. 15 DSGVO)</li>
                  <li data-id="element-68">Berichtigung (Art. 16 DSGVO)</li>
                  <li data-id="element-69">Löschung (Art. 17 DSGVO)</li>
                  <li data-id="element-70">Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                  <li data-id="element-71">Datenübertragbarkeit (Art. 20 DSGVO)</li>
                  <li data-id="element-72">Widerspruch (Art. 21 DSGVO)</li>
                </ul>
                <p className="mt-3" data-id="element-73">
                  Zur Ausübung Ihrer Rechte genügt eine formlose Mitteilung an
                  die oben genannten Kontaktdaten.
                </p>
              </section>

              <section data-id="element-74">
                <h3 className="text-base font-bold text-petrol mb-3" data-id="element-75">
                  6. Beschwerderecht
                </h3>
                <p data-id="element-76">
                  Sie haben das Recht, sich bei einer
                  Datenschutz-Aufsichtsbehörde zu beschweren.
                </p>
              </section>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}